<template>
  <div
    :class="[
      clsx('grid grid-cols-[1.125rem_1fr] items-center gap-y-1 sm:grid-cols-[1rem_1fr] mb-4'),
      clsx(label && 'gap-x-2'),
      $attrs.class
    ]"
  >
    <input
      v-bind="$attrs"
      v-model="model"
      type="checkbox"
      :class="clsx([base, 'col-start-1 row-start-1 justify-self-center mt-1'])"
    />

    <label
      v-if="label"
      :class="clsx(
        ['select-none text-base/6 text-slate-950 sm:text-sm/6', 'col-start-2 row-start-1 justify-self-start',],
        description  && 'font-medium',
        $attrs.disabled && 'opacity-50'
      )"
    >
      {{ label }}
    </label>

    <p
      v-if="description"
      :class="clsx(
        ['text-base/6 text-slate-500 sm:text-sm/6', 'col-start-1 row-start-2 col-span-2 justify-self-start'],
        $attrs.disabled && 'opacity-50'
      )"
    >
      {{ description }}
    </p>

    <p
      v-if="error"
      :class="clsx(
        ['text-base/6 text-error sm:text-sm/6', 'col-start-2 row-start-3 justify-self-start'],
        $attrs.disabled && 'opacity-50',
        !description && 'mt-2'
      )"
    >
      {{ error }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import clsx from 'clsx'

defineOptions({
  inheritAttrs: false
})

const model = defineModel()

defineProps({
  label: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  }
})

const base = [
  // Basic layout
  'relative isolate flex size-[1.125rem] items-center justify-center rounded-[0.3125rem] sm:size-4',
  // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
  'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(0.3125rem-1px)] before:bg-white before:shadow',
  // Background color when checked
  'before:checked:bg-[--checkbox-checked-bg]',
  // Border
  'border border-slate-950/15 checked:border-transparent checked:hover:border-transparent hover:border-slate-950/30 checked:bg-[--checkbox-checked-border]',
  // Inner highlight shadow
  'after:absolute after:inset-0 after:rounded-[calc(0.3125rem-1px)] after:shadow-[inset_0_1px_theme(colors.white/15%)]',
  'checked:after:bg-[url("data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTQgMTQiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTMgOEw2IDExTDExIDMuNSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==")]',
  // Indeterminate state with custom SVG (ligne horizontale)
  'indeterminate:after:bg-[url("data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTQgMTQiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTMgN0gxMSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==")]',
  // Focus ring
  'focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-primary-700',
  // Disabled state
  'disabled:opacity-60',
  'disabled:border-slate-950/25 disabled:bg-slate-950/6 disabled:[--checkbox-check:theme(colors.slate.950/60%)] disabled:before:bg-transparent',
  // colors
  '[--checkbox-check:theme(colors.white)] [--checkbox-checked-bg:theme(colors.primary.800)] [--checkbox-checked-border:theme(colors.primary.700)]'
]
</script>
